import React, { useState, useEffect } from 'react';
import { Upload, Search, Download, Trash2, FileText, Calendar, User, Plus, X, AlertCircle, Save, Edit } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface ImplantRecord {
  id: string;
  patient_name: string;
  implant_confirmed_date: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  doctor_notes: string;
  assigned_doctor_id: string | null;
  created_at: string;
  updated_at: string;
  notes_updated_at: string | null;
  doctor_admins?: {
    full_name: string;
  };
}

interface DoctorAdmin {
  id: string;
  full_name: string;
  email: string;
}

const ImplantList = () => {
  const { user } = useAuth();
  const [implants, setImplants] = useState<ImplantRecord[]>([]);
  const [doctors, setDoctors] = useState<DoctorAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedImplant, setSelectedImplant] = useState<ImplantRecord | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const [patientName, setPatientName] = useState('');
  const [implantDate, setImplantDate] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [assignedDoctor, setAssignedDoctor] = useState('');

  const [editingNotes, setEditingNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingDate, setEditingDate] = useState('');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [savingDate, setSavingDate] = useState(false);

  useEffect(() => {
    fetchImplants();
    fetchDoctors();
  }, []);

  const fetchImplants = async () => {
    try {
      const { data, error } = await supabase
        .from('implant_records')
        .select('*, doctor_admins(full_name)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImplants(data || []);
    } catch (error) {
      console.error('Error fetching implants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const { data, error } = await supabase
        .from('doctor_admins')
        .select('id, full_name, email')
        .order('full_name');

      if (error) throw error;
      setDoctors(data || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.name.endsWith('.zip')) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please select a ZIP file containing DICOM files');
        setSelectedFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!patientName.trim()) {
      setError('Please enter patient name');
      return;
    }
    if (!implantDate) {
      setError('Please select implant confirmed date');
      return;
    }
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const filePath = `implants/${Date.now()}_${selectedFile.name}`;

      const { error: uploadError } = await supabase.storage
        .from('dicom-files')
        .upload(filePath, selectedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase
        .from('implant_records')
        .insert({
          patient_name: patientName.trim(),
          implant_confirmed_date: implantDate,
          file_name: selectedFile.name,
          file_type: selectedFile.type,
          file_size: selectedFile.size,
          storage_path: filePath,
          assigned_doctor_id: assignedDoctor || null,
          uploaded_by: user?.id
        });

      if (dbError) throw dbError;

      await fetchImplants();
      setShowUploadModal(false);
      setPatientName('');
      setImplantDate('');
      setSelectedFile(null);
      setAssignedDoctor('');
    } catch (error: any) {
      console.error('Error uploading implant:', error);
      let errorMessage = error.message || 'Failed to upload implant record';

      if (errorMessage.includes('exceeded the maximum allowed size')) {
        errorMessage = 'File size exceeds project upload limit. Please go to Supabase Dashboard > Settings > Storage and increase the "File upload size limit" setting (Pro plan supports up to 5GB). Then try uploading again.';
      } else if (errorMessage.includes('quota')) {
        errorMessage = 'Storage quota exceeded. Please check your Supabase Dashboard > Settings > Usage or delete old files to free up space.';
      } else if (errorMessage.includes('Payload')) {
        errorMessage = 'File too large for current configuration. Go to Supabase Dashboard > Settings > Storage and set "File upload size limit" to 5000 MB (5GB).';
      }

      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (implant: ImplantRecord) => {
    try {
      const { data, error } = await supabase.storage
        .from('dicom-files')
        .download(implant.storage_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = implant.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const handleDelete = async (implant: ImplantRecord) => {
    if (!confirm(`Are you sure you want to delete the implant record for ${implant.patient_name}?`)) {
      return;
    }

    try {
      const { error: storageError } = await supabase.storage
        .from('dicom-files')
        .remove([implant.storage_path]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('implant_records')
        .delete()
        .eq('id', implant.id);

      if (dbError) throw dbError;

      await fetchImplants();
    } catch (error) {
      console.error('Error deleting implant:', error);
      alert('Failed to delete implant record');
    }
  };

  const handleViewDetails = (implant: ImplantRecord) => {
    setSelectedImplant(implant);
    setEditingNotes(implant.doctor_notes || '');
    setEditingDate(implant.implant_confirmed_date);
    setIsEditingDate(false);
    setShowDetailModal(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedImplant) return;

    setSavingNotes(true);
    try {
      const { error } = await supabase
        .from('implant_records')
        .update({ doctor_notes: editingNotes })
        .eq('id', selectedImplant.id);

      if (error) throw error;

      await fetchImplants();
      setSelectedImplant({ ...selectedImplant, doctor_notes: editingNotes });
      alert('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveDate = async () => {
    if (!selectedImplant || !editingDate) return;

    setSavingDate(true);
    try {
      const { error } = await supabase
        .from('implant_records')
        .update({ implant_confirmed_date: editingDate })
        .eq('id', selectedImplant.id);

      if (error) throw error;

      await fetchImplants();
      setSelectedImplant({ ...selectedImplant, implant_confirmed_date: editingDate });
      setIsEditingDate(false);
      alert('Date updated successfully');
    } catch (error) {
      console.error('Error saving date:', error);
      alert('Failed to update date');
    } finally {
      setSavingDate(false);
    }
  };

  const filteredImplants = implants.filter(implant =>
    implant.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    implant.doctor_admins?.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FileText className="text-green-600" />
              Implant List
            </h2>
            <p className="text-gray-600 mt-1">Manage patient implant records and DICOM files</p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Plus size={20} />
            New Implant
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by patient name or doctor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : filteredImplants.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No implant records found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Implant Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    File
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Doctor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Upload Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredImplants.map((implant) => (
                  <tr key={implant.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User size={16} className="text-gray-400 mr-2" />
                        <span className="font-medium text-gray-900">{implant.patient_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center text-gray-600">
                        <Calendar size={16} className="mr-2" />
                        {formatDate(implant.implant_confirmed_date)}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{implant.file_name}</div>
                      <div className="text-xs text-gray-500">{formatFileSize(implant.file_size)}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {implant.doctor_admins?.full_name || 'Unassigned'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(implant.created_at)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleViewDetails(implant)}
                          className="text-green-600 hover:text-green-800"
                          title="View Details"
                        >
                          <FileText size={18} />
                        </button>
                        <button
                          onClick={() => handleDownload(implant)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Download"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(implant)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">New Implant Record</h3>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setPatientName('');
                    setImplantDate('');
                    setSelectedFile(null);
                    setAssignedDoctor('');
                    setError('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-red-800 text-sm">{error}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patient Name *
                  </label>
                  <input
                    type="text"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter patient name"
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Implant Confirmed Date *
                  </label>
                  <input
                    type="date"
                    value={implantDate}
                    onChange={(e) => setImplantDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign to Doctor (Optional)
                  </label>
                  <select
                    value={assignedDoctor}
                    onChange={(e) => setAssignedDoctor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={uploading}
                  >
                    <option value="">Unassigned</option>
                    {doctors.map((doctor) => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    DICOM File (ZIP) *
                  </label>
                  <input
                    type="file"
                    accept=".zip"
                    onChange={handleFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={uploading}
                  />
                  {selectedFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </p>
                  )}
                  <p className="mt-2 text-xs text-blue-600">
                    💡 Supports files up to 5GB with Pro plan. Large files use automatic resumable upload for reliability.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleUpload}
                  disabled={uploading || !patientName || !implantDate || !selectedFile}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      Upload Implant Record
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setPatientName('');
                    setImplantDate('');
                    setSelectedFile(null);
                    setAssignedDoctor('');
                    setError('');
                  }}
                  disabled={uploading}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedImplant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Implant Record Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Patient Name</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedImplant.patient_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Implant Confirmed Date</label>
                    {isEditingDate ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={editingDate}
                          onChange={(e) => setEditingDate(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          disabled={savingDate}
                        />
                        <button
                          onClick={handleSaveDate}
                          disabled={savingDate}
                          className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300"
                          title="Save Date"
                        >
                          <Save size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingDate(false);
                            setEditingDate(selectedImplant.implant_confirmed_date);
                          }}
                          disabled={savingDate}
                          className="border border-gray-300 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                          title="Cancel"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold text-gray-900">{formatDate(selectedImplant.implant_confirmed_date)}</p>
                        <button
                          onClick={() => setIsEditingDate(true)}
                          className="text-green-600 hover:text-green-700"
                          title="Edit Date"
                        >
                          <Edit size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">File Name</label>
                    <p className="text-gray-900">{selectedImplant.file_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">File Size</label>
                    <p className="text-gray-900">{formatFileSize(selectedImplant.file_size)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Assigned Doctor</label>
                    <p className="text-gray-900">{selectedImplant.doctor_admins?.full_name || 'Unassigned'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Upload Date</label>
                    <p className="text-gray-900">{formatDate(selectedImplant.created_at)}</p>
                  </div>
                </div>

                {selectedImplant.notes_updated_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Notes Last Updated</label>
                    <p className="text-gray-900">{formatDate(selectedImplant.notes_updated_at)}</p>
                  </div>
                )}
              </div>

              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Doctor Notes</label>
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="flex items-center gap-2 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors disabled:bg-gray-300"
                  >
                    <Save size={16} />
                    {savingNotes ? 'Saving...' : 'Save Notes'}
                  </button>
                </div>
                <textarea
                  value={editingNotes}
                  onChange={(e) => setEditingNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows={8}
                  placeholder="Add clinical notes, observations, or treatment details..."
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => handleDownload(selectedImplant)}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Download size={20} />
                  Download DICOM File
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImplantList;
